/**
 * i18n & permission helpers — ported from ThemeMaker's
 * {@code lms-portal/src/lib/platform/i18n.ts}.
 *
 * <p>Two cross-cutting concerns chrome (and any block authored content)
 * needs but the renderer types alone don't model:
 *
 * <ol>
 *   <li><b>Localized strings</b> — tenant authors mark a label as
 *       localized by giving it an object instead of a string:
 *       <pre>{@code
 *         // plain
 *         { label: "Home", href: "/" }
 *
 *         // localized
 *         { label: { en: "Home", tr: "Ana Sayfa" }, href: "/" }
 *       }</pre>
 *       {@link t} resolves either form to a plain string for the active
 *       locale, with fallback to the tenant's default locale, then to
 *       any value in the object.</li>
 *
 *   <li><b>Permission gating</b> — menu items, footer links, top-bar
 *       CTAs can carry a {@code requireAuth} flag or a
 *       {@code requirePerms} array. {@link isVisible} applies both:
 *       <pre>{@code
 *         requireAuth: 'in'           → only signed-in users
 *         requireAuth: 'out'          → only anonymous visitors
 *         requireAuth: 'any'          → anyone (default)
 *         requirePerms: ['manager']   → must have at least one of these
 *       }</pre>
 *       Wildcard {@code "*"} in {@code user.permissions} passes any
 *       {@code requirePerms} check (admin/staff).</li>
 * </ol>
 *
 * <p>Both helpers live in one module because they're used together by
 * every header / footer / nav renderer — splitting would just add an
 * import at every call site.
 */

import type { CurrentUser } from './auth-types';
import type { LocalizedString } from './types';

// LocalizedString is re-exported from types.ts so block-side consumers
// don't have to think about which module owns the type. The shape used
// in chrome is identical to the shape used in block props.
export type { LocalizedString } from './types';

/** Style of the in-header language switcher. Picked by the tenant
 *  under the Languages section of the admin Shell editor. */
export type LanguageSwitcherStyle =
  | 'icon-popover'
  | 'code-chip'
  | 'icon-modal'
  | 'inline-segmented';

export interface I18nConfig {
  /** Fallback locale when a localized string lacks the active one.
   *  Defaults to {@code "en"} after merging. */
  default: string;
  /** Locales the tenant has authored content for. Drives the switcher's
   *  option list. Empty array = no switcher offered. */
  available: string[];
  /** Optional friendly labels for each locale code. Falls back to the
   *  locale's native name in the switcher if missing. */
  labels?: Record<string, string>;
  /** Whether to render the in-header switcher. Suppressed automatically
   *  when {@code available.length < 2}. */
  showSwitcher?: boolean;
  /** Visual style of the in-header switcher. Defaults to
   *  {@code 'icon-popover'}. */
  switchStyle?: LanguageSwitcherStyle;
}

export const DEFAULT_I18N: I18nConfig = {
  default: 'en',
  available: ['en'],
  labels: { en: 'English' },
};

/**
 * Resolve a possibly-localized string to a plain string for the active
 * locale. Order: requested locale → default locale → first available
 * value → empty string. Pure — safe in server and client components.
 */
export function t(
  value: LocalizedString | undefined | null,
  locale: string,
  fallback: string = 'en',
): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);

  if (locale && value[locale] != null) return value[locale];
  if (fallback && value[fallback] != null) return value[fallback];

  for (const k of Object.keys(value)) {
    if (value[k] != null) return value[k];
  }
  return '';
}

export type AuthRequirement = 'in' | 'out' | 'any';

/** Shape every gateable item satisfies. Menu items, footer links,
 *  top-bar CTAs all extend this. */
export interface Gated {
  /** Auth state required. Default {@code 'any'}. */
  requireAuth?: AuthRequirement;
  /** User must have AT LEAST ONE of these permissions. Empty / absent
   *  = no permission gate. {@code "*"} in user perms wildcards. */
  requirePerms?: string[];
  /** User must belong to AT LEAST ONE of these dynamic groups. Empty /
   *  absent = no audience gate. Same ANY-match semantics as
   *  {@code requirePerms}, checked against {@link CurrentUser.dynamicGroupIds}.
   *  Membership is resolved by the host per request (the runtime never
   *  fetches it) — hosts that don't populate {@code dynamicGroupIds}
   *  simply never satisfy this gate. */
  requireDynamicGroupIds?: number[];
  /** User must belong to AT LEAST ONE of these security groups. Empty /
   *  absent = no audience gate. Same ANY-match semantics as
   *  {@code requireDynamicGroupIds}, checked against
   *  {@link CurrentUser.securityGroupIds} — a DIFFERENT axis from
   *  dynamic groups (static membership vs. rule-computed audience). */
  requireSecurityGroupIds?: number[];
  /** ISO date/date-time string. Once {@code Date.now()} passes this
   *  instant the item stops satisfying the gate — permanently (there's
   *  no matching "not before" field; author a fresh item instead of
   *  un-expiring one). Absent = never expires. Primarily for
   *  time-boxed campaign content (hero banners, promo cards). */
  expiresAt?: string;
  /** Plugin id this item depends on. Hidden when the plugin is not
   *  enabled for the active tenant. Absent / empty = no plugin gate.
   *
   *  <p>Consulted against the ambient enabled-plugins set surfaced by
   *  {@code PluginGateContext} (set by the host once per request from
   *  its plugin-fetch helper). When the visibility check is called
   *  with {@code enabledPlugins === undefined} the gate is skipped —
   *  hosts that don't wire plugin gating get the legacy behaviour. */
  requiredPlugin?: string;
}

/**
 * Decide whether a gated item should render.
 *
 * <p>Three ambient gates apply in this order: auth → perms → plugin.
 * Each is a soft requirement (absent = pass).
 *
 * @param item the gated item
 * @param user the current user, or {@code null} when anonymous
 * @param enabledPlugins ambient plugin set. Pass {@code undefined} to
 *   skip the plugin gate entirely (legacy behaviour); pass an array
 *   (possibly empty) to enforce it. {@link Set} accepted for callers
 *   that want O(1) membership.
 */
export function isVisible(
  item: Gated,
  user: CurrentUser | null,
  enabledPlugins?: ReadonlyArray<string> | ReadonlySet<string>,
): boolean {
  const auth = item.requireAuth ?? 'any';
  if (auth === 'in' && !user) return false;
  if (auth === 'out' && user) return false;

  const perms = item.requirePerms;
  if (perms && perms.length > 0) {
    if (!user) return false;
    const userPerms = user.permissions ?? [];
    if (!userPerms.includes('*') && !perms.some((p) => userPerms.includes(p))) return false;
  }

  const dynamicGroupIds = item.requireDynamicGroupIds;
  if (dynamicGroupIds && dynamicGroupIds.length > 0) {
    if (!user) return false;
    const userGroupIds = user.dynamicGroupIds ?? [];
    if (!dynamicGroupIds.some((g) => userGroupIds.includes(g))) return false;
  }

  const securityGroupIds = item.requireSecurityGroupIds;
  if (securityGroupIds && securityGroupIds.length > 0) {
    if (!user) return false;
    const userSecurityGroupIds = user.securityGroupIds ?? [];
    if (!securityGroupIds.some((g) => userSecurityGroupIds.includes(g))) return false;
  }

  if (item.expiresAt) {
    const exp = Date.parse(item.expiresAt);
    if (!Number.isNaN(exp) && Date.now() > exp) return false;
  }

  const plugin = item.requiredPlugin;
  if (plugin && enabledPlugins !== undefined) {
    const hit =
      enabledPlugins instanceof Set
        ? (enabledPlugins as ReadonlySet<string>).has(plugin)
        : (enabledPlugins as ReadonlyArray<string>).includes(plugin);
    if (!hit) return false;
  }

  return true;
}

/** Keep only the items the current user is allowed to see. Stable
 *  order, no copies of objects.
 *
 *  <p>{@code enabledPlugins} is forwarded to {@link isVisible}; same
 *  "undefined = skip" semantics. */
export function filterVisible<T extends Gated>(
  items: T[] | undefined,
  user: CurrentUser | null,
  enabledPlugins?: ReadonlyArray<string> | ReadonlySet<string>,
): T[] {
  if (!items) return [];
  return items.filter((it) => isVisible(it, user, enabledPlugins));
}
